import { formatDelta, type MatchItem } from './scoring-types';

export type PlayerSide = 1 | 2;

export function normalizeSearch(value: string): string {
  return value
    .toLowerCase()
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
}

export function filterMatches(matches: MatchItem[], query: string): MatchItem[] {
  const needle = normalizeSearch(query);

  if (needle === '') {
    return matches;
  }

  return matches.filter((match) =>
    [...match.side1, ...match.side2].some((player) =>
      normalizeSearch(player.name).includes(needle),
    ),
  );
}

export function sideOfPlayer(match: MatchItem, leaguePlayerId: number): PlayerSide | null {
  if (match.side1.some((player) => player.memberId === leaguePlayerId)) {
    return 1;
  }

  if (match.side2.some((player) => player.memberId === leaguePlayerId)) {
    return 2;
  }

  return null;
}

export function matchesOfPlayer(matches: MatchItem[], leaguePlayerId: number): MatchItem[] {
  return matches.filter((match) => sideOfPlayer(match, leaguePlayerId) !== null);
}

export function opponentName(match: MatchItem, leaguePlayerId: number): string {
  if (sideOfPlayer(match, leaguePlayerId) === 2) {
    return match.player1Name;
  }

  return match.player2Name;
}

export function opponentRating(match: MatchItem, leaguePlayerId: number): number | null {
  const side = sideOfPlayer(match, leaguePlayerId);

  if (side === null || !match.rating) {
    return null;
  }

  return side === 1 ? match.rating.side2.rating : match.rating.side1.rating;
}

export function playerDeltaLabel(match: MatchItem, leaguePlayerId: number): string {
  const side = sideOfPlayer(match, leaguePlayerId);
  const rating = match.rating;

  if (side === null || !rating) {
    return '';
  }

  const view = side === 1 ? rating.side1 : rating.side2;

  if (rating.appliedDelta === null) {
    return `${formatDelta(view.deltaIfWin)} / ${formatDelta(view.deltaIfLose)}`;
  }

  return formatDelta(side === 1 ? rating.appliedDelta.side1 : rating.appliedDelta.side2);
}

export function playerResultLabel(match: MatchItem, leaguePlayerId: number): string {
  const side = sideOfPlayer(match, leaguePlayerId);

  if (side === null || match.winnerSide === null) {
    return 'bez wyniku';
  }

  const won = (match.winnerSide === 'SIDE1' ? 1 : 2) === side;
  const sets = match.sets
    .map((set) => (side === 1 ? `${set.p1}:${set.p2}` : `${set.p2}:${set.p1}`))
    .join('  ');

  return `${won ? 'wygrana' : 'porażka'} ${sets}`.trim();
}
